import { useState } from "react";
import { Check, X, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useUpdateUserPermissions, OrganizationMember, OrganizationMemberPermission } from "@/lib/api/betterAuth";

interface PermissionsEditorProps {
  member: OrganizationMember;
  organizationId: string;
  onSuccess?: () => void;
  inline?: boolean;
}

const PERMISSION_TYPES = {
  risk_management: {
    label: "Risk Management",
    description: "Access to risk management features and data",
    subTypes: ["read", "self"],
  },
  market_analysis: {
    label: "Market Analysis",
    description: "Access to market analysis tools and reports",
    subTypes: ["read", "self"],
  },
};

export function PermissionsEditor({
  member,
  organizationId,
  onSuccess,
  inline = false,
}: PermissionsEditorProps) {
  const { toast } = useToast();
  const updatePermissions = useUpdateUserPermissions();
  // Convert array of permissions to object for easier UI state management
  const permissionsToObject = (permissions: OrganizationMemberPermission[]) => {
    const obj: Record<string, { enabled: boolean; subTypes: string[] }> = {};
    permissions.forEach(perm => {
      obj[perm.permission_type] = {
        enabled: true,
        subTypes: perm.subTypes || []
      };
    });
    return obj;
  };

  const [localPermissions, setLocalPermissions] = useState(
    permissionsToObject(member.permissions || [])
  );
  const [hasChanges, setHasChanges] = useState(false);

  const handlePermissionToggle = async (
    permissionType: string,
    enabled: boolean
  ) => {
    if (inline) {
      // For inline editing, save immediately
      try {
        const result = await updatePermissions.mutateAsync({
          organizationId,
          memberId: member.id,
          permissionType,
          enabled,
          subTypes: enabled ? PERMISSION_TYPES[permissionType as keyof typeof PERMISSION_TYPES]?.subTypes : [],
        });

        if (result.success) {
          toast({
            title: "Success",
            description: `Permission ${enabled ? "granted" : "removed"} successfully`,
          });
          onSuccess?.();
        } else {
          toast({
            title: "Error",
            description: result.message || "Failed to update permission",
            variant: "destructive",
          });
        }
      } catch (error: any) {
        toast({
          title: "Error",
          description: error?.message || "Failed to update permission",
          variant: "destructive",
        });
      }
    } else {
      // For modal editing, just update local state
      setLocalPermissions((prev) => ({
        ...prev,
        [permissionType]: {
          enabled,
          subTypes: enabled ? PERMISSION_TYPES[permissionType as keyof typeof PERMISSION_TYPES]?.subTypes || [] : [],
        },
      }));
      setHasChanges(true);
    }
  };

  const handleSaveChanges = async () => {
    if (!hasChanges) return;

    try {
      // Save all changed permissions
      const originalPermissionsObj = permissionsToObject(member.permissions || []);
      const promises = Object.entries(localPermissions).map(
        ([permissionType, permission]) => {
          const originalPermission = originalPermissionsObj[permissionType];
          if (originalPermission?.enabled !== permission.enabled) {
            return updatePermissions.mutateAsync({
              organizationId,
              memberId: member.id,
              permissionType,
              enabled: permission.enabled,
              subTypes: permission.enabled ? permission.subTypes : [],
            });
          }
          return null;
        }
      );

      const validPromises = promises.filter(Boolean);
      if (validPromises.length > 0) {
        await Promise.all(validPromises);
      }

      toast({
        title: "Success",
        description: "Permissions updated successfully",
      });
      setHasChanges(false);
      onSuccess?.();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to update permissions",
        variant: "destructive",
      });
    }
  };

  const handleResetChanges = () => {
    setLocalPermissions(permissionsToObject(member.permissions || []));
    setHasChanges(false);
  };

  const currentPermissions = inline ? permissionsToObject(member.permissions || []) : localPermissions;

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {/* Header for table-like layout */}
        <div className="grid grid-cols-12 gap-4 px-4 py-2 bg-gray-50 rounded-lg text-sm font-medium text-gray-700">
          <div className="col-span-6">Permission</div>
          <div className="col-span-4">Access Level</div>
          <div className="col-span-2 text-right">Enabled</div>
        </div>

        {/* Scrollable permissions list */}
        <div className={`space-y-1 ${!inline ? 'max-h-[60vh] overflow-y-auto' : 'max-h-[70vh] overflow-y-auto'}`}>
          {Object.entries(PERMISSION_TYPES).map(([key, config]) => {
            const permission = currentPermissions[key];
            const isEnabled = permission?.enabled || false;

            return (
              <div
                key={key}
                className="grid grid-cols-12 gap-4 px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {/* Permission Name with Tooltip */}
                <div className="col-span-6 flex items-center gap-2">
                  <span className="font-medium text-gray-900">{config.label}</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      <p>{config.description}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>

                {/* Access Level */}
                <div className="col-span-4 flex items-center">
                  {isEnabled ? (
                    <div className="flex items-center gap-1">
                      <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">
                        {config.subTypes.join(", ")}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <div className="h-2 w-2 bg-gray-300 rounded-full"></div>
                      <span className="text-sm text-gray-400">No access</span>
                    </div>
                  )}
                </div>

                {/* Toggle Switch */}
                <div className="col-span-2 flex justify-end items-center">
                  <Switch
                    checked={isEnabled}
                    onCheckedChange={(enabled) =>
                      handlePermissionToggle(key, enabled)
                    }
                    disabled={updatePermissions.isLoading}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Action buttons for modal mode */}
        {!inline && hasChanges && (
          <div className="flex justify-between pt-4 border-t">
            <Button
              variant="outline"
              onClick={handleResetChanges}
              disabled={updatePermissions.isLoading}
            >
              <X className="h-4 w-4 mr-2" />
              Reset Changes
            </Button>
            <Button
              onClick={handleSaveChanges}
              disabled={updatePermissions.isLoading}
            >
              <Check className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}